import React, { useState, useEffect, FormEvent, ChangeEvent, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ActiveChatInputBar.module.css';
import { playablesIcon, sendIcon, imageIcon, dotsIcon } from '../../assets/icons';
import ImageAttachment from './ImageAttachment';
import GeneratingImageLoadingBar from './GeneratingImageLoadingBar';
import SendingImageLoadingBar from './SendingImageLoadingBar';
import SpotifyTrackPreview from './SpotifyTrackPreview';
import { useImageAttachment } from '../../hooks/useImageAttachment';
import { api } from '../../api';
import { ChatState, Inputable, Playable } from '../../types/active-chat';
import { SpotifyTrack } from '../../api/types/chat';

interface ActiveChatInputBarProps {
  message: string;
  setMessage: React.Dispatch<React.SetStateAction<string>>;
  handleSend: (e: FormEvent, extras?: Record<string, any>) => Promise<void>;
  handleImageSend: (image: File, message: string) => Promise<void>;
  handleGenerateImage: (prompt: string) => Promise<void>;
  showPlayablesPopup: boolean;
  setShowPlayablesPopup: React.Dispatch<React.SetStateAction<boolean>>;
  chat: ChatState | null;
  onFocus?: () => void;
  onBlur?: () => void;
  isPreview?: boolean;
  activePlayable: Playable | null;
  activeInputable: Inputable | null;
  onImageAttachmentChange?: (hasAttachment: boolean) => void;
  setShowLoginModal?: (show: boolean) => void;
}

function ActiveChatInputBar({ 
  message, 
  setMessage, 
  handleSend,
  handleImageSend,
  handleGenerateImage,
  showPlayablesPopup, 
  setShowPlayablesPopup, 
  chat,
  onFocus, 
  onBlur, 
  isPreview,
  activePlayable,
  activeInputable,
  onImageAttachmentChange,
  setShowLoginModal
}: ActiveChatInputBarProps) {
  const navigate = useNavigate();
  const [hideButtons, setHideButtons] = useState<boolean>(false);
  const [showSecondaryInput, setShowSecondaryInput] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { image, fileInputRef, onSelect, onRemove, onTriggerSelect } = useImageAttachment();
  const [isGeneratingImage, setIsGeneratingImage] = useState<boolean>(false);
  const [generatingPrompt, setGeneratingPrompt] = useState<string>('');
  const [selectedTrack, setSelectedTrack] = useState<SpotifyTrack | null>(null);
  const [isSearchingTrack, setIsSearchingTrack] = useState<boolean>(false);
  
  // Prevent touch scrolling on input bar elements
  const preventTouchScroll = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Notify parent about image attachment changes
  useEffect(() => {
    onImageAttachmentChange?.(Boolean(image));
  }, [image, onImageAttachmentChange]);

  // Handle initial inputable selection
  useEffect(() => {
    // Show secondary input for all inputables except upload-image
    // since upload-image doesn't need text input
    setShowSecondaryInput(activeInputable !== null && activeInputable.id !== 'upload-image');
  }, [activeInputable]);
  
  // Add debounced Spotify search
  useEffect(() => {
    if (!showSecondaryInput || activeInputable?.id !== 'spotify' || !message.trim()) {
      return;
    }

    const searchTimeout = setTimeout(async () => {
      try {
        setIsSearchingTrack(true);
        const track = await api.spotify.searchTrack(message);
        setSelectedTrack(track);
      } catch (error) {
        console.error('Failed to search track:', error);
      } finally {
        setIsSearchingTrack(false);
      }
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [message, showSecondaryInput, activeInputable]);

  const handlePlayablesClick = () => {
    if (isPreview) {
      setShowLoginModal?.(true);
      return;
    }
    setShowPlayablesPopup(!showPlayablesPopup);
    setShowSecondaryInput(false);
  };

  const clearMessage = (formElement: HTMLFormElement | null) => {
    setMessage('');
    // First find the textarea element
    const textarea = formElement?.querySelector('textarea');
    // Then reset its height
    if (textarea) {
      // Set height to auto first to properly calculate the new height
      textarea.style.height = 'auto';
      // Then set to min-height
      textarea.style.height = 'var(--textarea-min-height)';
    }
  };

  const handleMessageSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    if (image) {
      try {
        setIsSubmitting(true);
        await handleImageSend(image, message);
        // Reset all states after successful submission
        onRemove();
        clearMessage(e.target as HTMLFormElement);
        setShowSecondaryInput(false);
        setHideButtons(false);
      } catch (error) {
        console.error('Failed to send image:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else if (activeInputable?.id === 'spotify' && selectedTrack) {
      try {
        setIsSubmitting(true);
        clearMessage(e.target as HTMLFormElement);
        setShowSecondaryInput(false);
        setHideButtons(false);
        
        await handleSend(e, { spotifyTrack: selectedTrack });
        setSelectedTrack(null);
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else if (activeInputable?.id === 'generate-image' && showSecondaryInput && message.trim()) {
      try {
        setIsSubmitting(true);
        const promptMessage = message;
        setIsGeneratingImage(true);
        setGeneratingPrompt(promptMessage);
        clearMessage(e.target as HTMLFormElement);
        setShowSecondaryInput(false);
        setHideButtons(false);
        
        try {
          await handleGenerateImage(promptMessage);
          // Clear UI immediately after prompt is sent
          setIsGeneratingImage(false);
          setGeneratingPrompt('');
        } catch (error) {
          console.error('Failed to generate image:', error);
          throw error;
        }
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        setIsSubmitting(true);
        clearMessage(e.target as HTMLFormElement);
        setShowSecondaryInput(false);
        setHideButtons(false);
        
        await handleSend(e);
      } catch (error) {
        console.error('Failed to send message:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSecondaryInputClick = () => {
    if (isPreview) {
      setShowLoginModal?.(true);
      return;
    }
    if (activeInputable?.id === 'upload-image') {
      onTriggerSelect();
    } else {
      setShowSecondaryInput(!showSecondaryInput);
    }
  };

  const handleInputFocus = () => {
    setHideButtons(true);
    
    // On mobile, ensure the textarea is scrollable if content is overflowing
    const textarea = document.querySelector('textarea.' + styles.messageInput) as HTMLTextAreaElement;
    if (textarea && textarea.scrollHeight > textarea.clientHeight) {
      textarea.style.overflow = 'auto';
    }
    
    onFocus?.();
  };

  const handleInputBlur = () => {
    setHideButtons(false);
    onBlur?.();
  };

  const handleImageRemove = () => {
    onRemove();
    setShowSecondaryInput(false);
  };

  // Simplify button visibility logic
  const showSecondaryButton = !activeInputable && !image && !activePlayable; // Show default upload button when no inputable/playable is active
  const showInputableButton = activeInputable && !image && !activePlayable; // Show inputable button when one is active and no playable

  const getPlaceholder = () => {
    if (showSecondaryInput && activeInputable?.inputPlaceholder) {
      return activeInputable.inputPlaceholder;
    }
    return image ? 'Add a comment...' : `Message ${chat?.display_name}`;
  };

  // Get the appropriate send icon based on active inputable
  const getSendIcon = () => {
    if (image) {
      return sendIcon;
    }
    if (activeInputable && activeInputable.id !== 'upload-image' && showSecondaryInput) {
      return activeInputable.inputIcon || sendIcon;
    }
    if (message.trim() === '') {
      return dotsIcon;
    }
    return sendIcon;
  };

  // Helper function to determine if the send button should be disabled
  const isSendButtonDisabled = () => {
    if (isSubmitting) return true;
    
    // For Spotify, require track selection only when track is needed
    if (activeInputable?.id === 'spotify' && selectedTrack === null && showSecondaryInput) {
      return true;
    }

    // For generate-image, require a prompt only in secondary input mode
    if (activeInputable?.id === 'generate-image' && showSecondaryInput) {
      return !message.trim();
    }

    // For other cases, allow empty messages (for continue functionality)
    return false;
  };

  // Notify parent about preview states
  useEffect(() => {
    onImageAttachmentChange?.(Boolean(image || (activeInputable?.id === 'spotify' && selectedTrack)));
  }, [image, selectedTrack, activeInputable, onImageAttachmentChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // On mobile, Enter creates new paragraph
    // On desktop, Shift+Enter creates new paragraph
    if (e.key === 'Enter') {
      if (window.innerWidth <= 480 || e.shiftKey) {
        return; // Allow default behavior to insert newline
      }
      e.preventDefault();
      handleMessageSubmit(e);
    }
  };

  // Auto-resize textarea as content changes
  const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const textarea = e.target;
    
    // Reset to min-height using CSS variable
    textarea.style.height = 'var(--textarea-min-height)';
    
    // Get the computed min and max heights from CSS
    const computedStyle = window.getComputedStyle(textarea);
    const minHeight = parseInt(computedStyle.getPropertyValue('--textarea-min-height'));
    const maxHeight = parseInt(computedStyle.getPropertyValue('--textarea-max-height'));
    
    // Calculate new height based on content
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    
    // Only update height if content needs more space
    if (newHeight > minHeight) {
      // If we're at max height, use the CSS variable to trigger the scrollbar
      if (newHeight === maxHeight) {
        textarea.style.height = 'var(--textarea-max-height)';
        textarea.style.overflowY = 'auto';
      } else {
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = 'hidden';
      }
    } else {
      textarea.style.height = 'var(--textarea-min-height)';
      textarea.style.overflowY = 'hidden';
    }
  };

  return (
    <div 
      className={styles.container} 
      onTouchMove={preventTouchScroll}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {image && !isSubmitting && (
        <ImageAttachment 
          image={image}
          onRemove={handleImageRemove}
        />
      )}
      {selectedTrack && activeInputable?.id === 'spotify' && (
        <SpotifyTrackPreview 
          track={selectedTrack}
          onRemove={() => setSelectedTrack(null)}
        />
      )}
      {image && isSubmitting ? (
        <SendingImageLoadingBar image={image} />
      ) : isGeneratingImage ? (
        <GeneratingImageLoadingBar prompt={generatingPrompt} />
      ) : (
        <form 
          onSubmit={handleMessageSubmit} 
          className={styles.inputArea}
          onTouchMove={preventTouchScroll}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={onSelect}
            accept="image/*"
            style={{ display: 'none' }}
            disabled={isSubmitting}
          />
          {!hideButtons && !image && (
            <>
              <button 
                type="button" 
                className={`${styles.playablesButton} ${showPlayablesPopup ? styles.active : ''}`}
                onClick={handlePlayablesClick}
                disabled={isSubmitting}
              >
                <img src={playablesIcon} alt="Catalog" />
              </button>
              {showSecondaryButton && (
                <button 
                  type="button" 
                  className={`${styles.secondaryInputButton} ${showSecondaryInput ? styles.active : ''}`}
                  onClick={() => onTriggerSelect()}
                  disabled={isSubmitting}
                >
                  <img src={imageIcon} alt="Upload image" />
                </button>
              )}
              {showInputableButton && (
                <button 
                  type="button" 
                  className={`${styles.secondaryInputButton} ${showSecondaryInput ? styles.active : ''}`}
                  onClick={handleSecondaryInputClick}
                  disabled={isSubmitting}
                >
                  <img src={activeInputable.inputIcon} alt={activeInputable.title} />
                </button>
              )}
            </>
          )}
          <div style={{ position: 'relative', flex: 1 }}>
            <textarea
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              className={styles.messageInput}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              disabled={isSubmitting}
              rows={1}
            />
            {((message.trim() || activeInputable?.id === 'spotify' || image || !message) && (
              <button 
                type="submit" 
                className={`${styles.sendButton} ${styles.visible}`}
                disabled={isSendButtonDisabled()}
              >
                <img src={getSendIcon()} alt="Send" />
              </button>
            ))}
          </div>
        </form>
      )}
    </div>
  );
}

export default ActiveChatInputBar; 